import { type InquirySubmission, sendInquiry } from './inquiry';

const INQUIRY_EMAIL = 'create@hackstacks.tech';

interface PortfolioLink {
  label: string;
  href: string;
}

interface PortfolioProject {
  id: string;
  index: string;
  name: string;
  category: string;
  statement: string;
  description: string;
  signals: string[];
  links: PortfolioLink[];
  screenshot: string;
}

const PORTFOLIO_PROJECTS: PortfolioProject[] = [
  {
    id: 'humanode',
    index: '01',
    name: 'Humanode',
    category: 'Protocol / ecosystem',
    statement: 'A blockchain secured by humans, not capital.',
    description:
      'A Rust-based network unifying biometric uniqueness, governance, infrastructure, and the products that make its ecosystem legible.',
    signals: ['Rust blockchain', 'Biometric uniqueness', 'Governance', 'Product ecosystem'],
    screenshot: '/assets/portfolio/humanode-website.jpg',
    links: [
      { label: 'Main system', href: 'https://humanode.io/' },
      { label: 'Ecosystem', href: 'https://ecosystem.humanode.io/' },
      { label: 'Roadmap', href: 'https://roadmap.humanode.io/' },
    ],
  },
  {
    id: 'seanode',
    index: '02',
    name: 'SeaNode',
    category: 'Operational product',
    statement: 'Know your vessel is ready.',
    description:
      'A ship-readiness platform turning maritime requirements, vessel data, documents, and deadlines into verified, submission-ready evidence.',
    signals: ['Domain intelligence', 'Operational workflow', 'Evidence trail', 'Fleet readiness'],
    screenshot: '/assets/portfolio/seanode-website.png',
    links: [{ label: 'Visit SeaNode', href: 'https://seanode.tech/' }],
  },
  {
    id: 'botkiller',
    index: '03',
    name: 'BOTK',
    category: 'Interactive culture',
    statement: 'Bots out. Humans in.',
    description:
      'A Humanode-native community world combining identity, wallet interactions, visual media, sound, and a realtime 3D browser game.',
    signals: ['3D browser game', 'Brand world', 'Wallet interaction', 'Community product'],
    screenshot: '/assets/portfolio/botkiller-website.jpg',
    links: [{ label: 'Enter BOTK', href: 'https://botkiller.club/' }],
  },
];

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function appendText<K extends keyof HTMLElementTagNameMap>(
  parent: HTMLElement,
  tag: K,
  className: string,
  text: string,
): HTMLElementTagNameMap[K] {
  const node = element(tag, className);
  node.textContent = text;
  parent.append(node);
  return node;
}

function buildProjectVisual(project: PortfolioProject): HTMLElement {
  const visual = element(
    'div',
    `portfolio-project__visual portfolio-project__visual--${project.id}`,
  );
  const browser = element('figure', 'portfolio-browser');
  const chrome = element('div', 'portfolio-browser__chrome');
  chrome.setAttribute('aria-hidden', 'true');
  const controls = element('span', 'portfolio-browser__controls');
  controls.append(element('i'), element('i'), element('i'));
  const domain = new URL(project.links[0]?.href ?? 'https://hackstacks.tech').hostname.replace(
    /^www\./,
    '',
  );
  appendText(chrome, 'span', 'portfolio-browser__domain', domain);
  chrome.prepend(controls);

  const screenshot = element('img', 'portfolio-browser__image');
  screenshot.src = project.screenshot;
  screenshot.alt = `${project.name} public website homepage`;
  screenshot.loading = 'lazy';
  screenshot.decoding = 'async';
  screenshot.width = 1440;
  screenshot.height = 900;
  browser.append(chrome, screenshot);

  visual.append(browser);

  if (project.id !== 'humanode') {
    const motion = element('div', `project-motion project-motion--${project.id}`);
    motion.setAttribute('aria-hidden', 'true');
    motion.append(element('i'), element('i'), element('i'), element('i'));
    visual.append(motion);
  }

  return visual;
}

function mountPortfolioAnimations(root: HTMLElement): void {
  const projects = [...root.querySelectorAll<HTMLElement>('.portfolio-project')];
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window)
  ) {
    projects.forEach((project) => {
      project.classList.add('is-visible');
    });
    return;
  }

  root.classList.add('has-portfolio-motion');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
  );
  projects.forEach((project) => {
    observer.observe(project);
  });
}

function buildProject(project: PortfolioProject): HTMLElement {
  const article = element('article', `portfolio-project portfolio-project--${project.id}`);
  article.id = `work-${project.id}`;

  const header = element('header', 'portfolio-project__header');
  appendText(header, 'span', 'portfolio-project__index', project.index);
  appendText(header, 'span', 'portfolio-project__category', project.category);
  appendText(header, 'h3', 'portfolio-project__name', project.name);
  appendText(header, 'p', 'portfolio-project__statement', project.statement);

  const detail = element('div', 'portfolio-project__detail');
  appendText(detail, 'p', 'portfolio-project__description', project.description);
  const signals = element('ul', 'portfolio-project__signals');
  for (const signal of project.signals) appendText(signals, 'li', '', signal);

  const links = element('div', 'portfolio-project__links');
  for (const link of project.links) {
    const anchor = element('a');
    anchor.href = link.href;
    anchor.target = '_blank';
    anchor.rel = 'noreferrer';
    anchor.textContent = link.label;
    anchor.setAttribute('aria-label', `${link.label} (opens in a new tab)`);
    links.append(anchor);
  }
  detail.append(signals, links);

  article.append(header, buildProjectVisual(project), detail);
  return article;
}

function buildPortfolio(): HTMLElement {
  const section = element('section', 'afterworld-section portfolio-shell');
  section.id = 'portfolio';
  section.setAttribute('aria-labelledby', 'portfolio-title');

  const heading = element('div', 'afterworld-heading');
  const title = appendText(heading, 'h2', 'afterworld-title', 'Precision leaves evidence.');
  title.id = 'portfolio-title';
  appendText(
    heading,
    'p',
    'afterworld-intro',
    '100+ projects. Three selected to show the range of scale, form, and operating logic.',
  );

  const projects = element('div', 'portfolio-projects');
  for (const project of PORTFOLIO_PROJECTS) projects.append(buildProject(project));
  section.append(heading, projects);
  return section;
}

const ENGAGEMENT_MODES = [
  {
    title: 'From idea to enduring product',
    description:
      'We challenge the premise, shape the system, form its team, build it, and can remain for its continued evolution.',
  },
  {
    title: 'Inside an existing company',
    description:
      'A dedicated team enters an established product or new initiative. If the relationship proves itself, transfer or buyout can remain available.',
  },
  {
    title: 'A precise intervention',
    description:
      'A landing page, internal system, security problem, or other bounded need receives the same deliberate attention as a large platform.',
  },
  {
    title: 'Alongside an agency',
    description:
      'We can operate invisibly as a white-label delivery team or work as an openly credited partner—you choose the shape.',
  },
] as const;

const FORMATION_DISCIPLINES = [
  ['Strategy', '0,2'],
  ['Product', '0,1'],
  ['Design', '0,2,3'],
  ['Engineering', '0,1,2,3'],
  ['QA', '0,1,2,3'],
  ['Operations', '0,1'],
  ['Security', '0,1,2'],
  ['Motion', '0,3'],
] as const;

function appendKineticWords(parent: HTMLElement, text: string, startIndex = 0): number {
  text.split(' ').forEach((word, index) => {
    const span = element('span', 'team-word');
    span.textContent = word;
    span.style.setProperty('--word-index', String(startIndex + index));
    parent.append(span, document.createTextNode(' '));
  });
  return startIndex + text.split(' ').length;
}

function buildTeamStatement(): HTMLElement {
  const section = element('section', 'afterworld-section team-statement-shell');
  section.id = 'team';
  section.setAttribute('aria-labelledby', 'team-statement-title');

  const statement = element('div', 'team-statement');
  const title = element('h2', 'team-statement__title');
  title.id = 'team-statement-title';
  const foundingLine = element('span', 'team-statement__line');
  const nextWordIndex = appendKineticWords(foundingLine, 'Founding team: 3');
  const rosterLine = element('span', 'team-statement__line');
  appendKineticWords(rosterLine, 'Roster: Hundreds', nextWordIndex);
  title.append(foundingLine, rosterLine);

  const copy = element('p', 'team-statement__copy');
  appendKineticWords(
    copy,
    'A permanent core holds accountability. Around it, a working network of approximately 200 technical and creative specialists lets each engagement take the shape it actually requires.',
  );
  statement.append(title, copy);
  section.append(statement);
  return section;
}

function buildEngagement(): HTMLElement {
  const section = element('section', 'afterworld-section engagement-shell');
  section.id = 'engagement';
  section.setAttribute('aria-label', 'Engagement formations');

  const system = element('div', 'engagement-system');
  system.dataset.active = '0';

  const narrative = element('div', 'engagement-system__narrative');
  const panels = element('div', 'engagement-mode-panels');
  const navigation = element('div', 'engagement-mode-navigation');
  navigation.setAttribute('role', 'tablist');
  navigation.setAttribute('aria-label', 'Engagement formations');

  ENGAGEMENT_MODES.forEach((mode, index) => {
    const panel = element('article', 'engagement-mode');
    panel.id = `engagement-mode-${index + 1}`;
    panel.setAttribute('role', 'tabpanel');
    appendText(panel, 'h3', 'engagement-mode__title', mode.title);
    appendText(panel, 'p', 'engagement-mode__description', mode.description);
    panels.append(panel);

    const button = element('button', 'engagement-mode-control');
    button.type = 'button';
    button.dataset.mode = String(index);
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', panel.id);
    button.setAttribute('aria-label', `${String(index + 1).padStart(2, '0')}: ${mode.title}`);
    appendText(button, 'span', '', String(index + 1).padStart(2, '0'));
    button.append(element('i'));
    navigation.append(button);
  });
  narrative.append(panels, navigation);

  const formation = element('div', 'engagement-formation');
  formation.setAttribute('aria-hidden', 'true');
  const formationStatus = element('div', 'engagement-formation__status');
  appendText(formationStatus, 'span', '', 'Core 03');
  appendText(formationStatus, 'span', '', 'Network 200+');

  const field = element('div', 'engagement-formation__field');
  field.style.setProperty('--formation-radius', '38%');
  const rings = element('div', 'formation-rings');
  rings.append(element('i'), element('i'), element('i'));
  field.append(rings);

  const core = element('div', 'formation-core');
  core.append(element('i'), element('i'), element('i'));
  appendText(core, 'span', '', 'Accountability');
  field.append(core);

  FORMATION_DISCIPLINES.forEach(([label, modes], index) => {
    const angle = (index / FORMATION_DISCIPLINES.length) * Math.PI * 2 - Math.PI / 2;
    const x = 50 + Math.cos(angle) * 38;
    const y = 50 + Math.sin(angle) * 38;

    const line = element('i', 'formation-connection');
    line.dataset.modes = modes;
    line.style.setProperty('--formation-angle', `${(angle * 180) / Math.PI}deg`);
    field.append(line);

    const node = element('div', 'formation-node');
    node.dataset.modes = modes;
    if (Math.cos(angle) > 0.9) node.dataset.labelSide = 'right';
    else if (Math.cos(angle) < -0.9) node.dataset.labelSide = 'left';
    else if (Math.sin(angle) < -0.9) node.dataset.labelSide = 'top';
    else if (Math.sin(angle) > 0.9) node.dataset.labelSide = 'bottom';
    else node.dataset.labelSide = 'diagonal';
    node.style.left = `${x}%`;
    node.style.top = `${y}%`;
    node.style.setProperty('--formation-label-x', `${Math.cos(angle) * 1.65}rem`);
    node.style.setProperty('--formation-label-y', `${Math.sin(angle) * 1.65}rem`);
    node.append(element('i'));
    appendText(node, 'span', '', label);
    field.append(node);
  });

  formation.append(formationStatus, field);
  system.append(narrative, formation);
  section.append(system);
  return section;
}

function mountTeamStatement(root: HTMLElement): void {
  const statement = root.querySelector<HTMLElement>('.team-statement');
  if (!statement) return;
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window)
  ) {
    statement.classList.add('is-visible');
    return;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      statement.classList.add('is-visible');
      observer.disconnect();
    },
    { threshold: 0.35 },
  );
  observer.observe(statement);
}

function mountEngagementAnimation(root: HTMLElement): void {
  const system = root.querySelector<HTMLElement>('.engagement-system');
  if (!system) return;

  const controls = [...system.querySelectorAll<HTMLButtonElement>('.engagement-mode-control')];
  const panels = [...system.querySelectorAll<HTMLElement>('.engagement-mode')];
  const network = [
    ...system.querySelectorAll<HTMLElement>('.formation-node, .formation-connection'),
  ];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoAdvance = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let active = 0;
  let timer = 0;

  const activate = (index: number): void => {
    active = (index + ENGAGEMENT_MODES.length) % ENGAGEMENT_MODES.length;
    system.dataset.active = String(active);
    controls.forEach((control, controlIndex) => {
      const selected = controlIndex === active;
      control.classList.toggle('is-active', selected);
      control.setAttribute('aria-selected', String(selected));
      control.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === active;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', String(!selected));
    });
    network.forEach((item) => {
      const modes = item.dataset.modes?.split(',') ?? [];
      item.classList.toggle('is-active', modes.includes(String(active)));
    });
  };

  const stop = (): void => window.clearInterval(timer);
  const play = (): void => {
    stop();
    if (
      reducedMotion ||
      !autoAdvance ||
      system.matches(':hover') ||
      system.contains(document.activeElement)
    ) {
      return;
    }
    timer = window.setInterval(() => activate(active + 1), 5200);
  };

  controls.forEach((control, index) => {
    control.addEventListener('click', () => {
      activate(index);
      play();
    });
  });
  system.addEventListener('pointerenter', stop);
  system.addEventListener('pointerleave', play);
  system.addEventListener('focusin', stop);
  system.addEventListener('focusout', play);
  activate(0);
  play();
}

function inputField(label: string, name: string, type = 'text', required = true): HTMLLabelElement {
  const field = element('label', 'inquiry-field');
  appendText(field, 'span', '', label);
  const input = element('input');
  input.name = name;
  input.type = type;
  input.required = required;
  if (type === 'email') input.autocomplete = 'email';
  if (name === 'name') input.autocomplete = 'name';
  if (name === 'company') input.autocomplete = 'organization';
  field.append(input);
  return field;
}

function textareaField(
  label: string,
  name: string,
  placeholder: string,
  required = true,
): HTMLLabelElement {
  const field = element('label', 'inquiry-field inquiry-field--wide');
  appendText(field, 'span', '', label);
  const textarea = element('textarea');
  textarea.name = name;
  textarea.rows = 6;
  textarea.maxLength = 1600;
  textarea.required = required;
  textarea.placeholder = placeholder;
  field.append(textarea);
  return field;
}

function selectField(
  label: string,
  name: string,
  placeholder: string,
  options: string[],
): HTMLLabelElement {
  const field = element('label', 'inquiry-field');
  appendText(field, 'span', '', label);
  const select = element('select');
  select.name = name;
  select.required = true;
  const empty = element('option');
  empty.value = '';
  empty.textContent = placeholder;
  empty.disabled = true;
  empty.selected = true;
  select.append(empty);
  for (const optionLabel of options) {
    const option = element('option');
    option.value = optionLabel;
    option.textContent = optionLabel;
    select.append(option);
  }
  field.append(select);
  return field;
}

function choiceFieldset(
  legendText: string,
  name: string,
  choices: Array<[string, string]>,
): HTMLFieldSetElement {
  const fieldset = element('fieldset', 'inquiry-modes');
  const legend = element('legend');
  legend.textContent = legendText;
  if (legendText) fieldset.append(legend);

  choices.forEach(([label, value], index) => {
    const option = element('label', 'inquiry-mode');
    const input = element('input');
    input.type = 'radio';
    input.name = name;
    input.value = value;
    input.required = true;
    if (index === 0) input.checked = true;
    appendText(option, 'span', '', label);
    option.prepend(input);
    fieldset.append(option);
  });
  return fieldset;
}

function inquiryPanel(index: number, title: string): HTMLElement {
  const panel = element('section', 'inquiry-step');
  panel.dataset.step = String(index);
  panel.setAttribute('aria-labelledby', `inquiry-step-${index}`);
  const heading = appendText(panel, 'h3', 'inquiry-step__title', title);
  heading.id = `inquiry-step-${index}`;
  return panel;
}

function inquiryButton(label: string, variant: 'back' | 'next'): HTMLButtonElement {
  const button = element('button', `inquiry-control inquiry-control--${variant}`);
  button.type = 'button';
  button.textContent = label;
  return button;
}

function buildInquiry(): HTMLElement {
  const section = element('section', 'afterworld-section inquiry-shell');
  section.id = 'inquiry';
  section.tabIndex = -1;
  section.setAttribute('aria-labelledby', 'inquiry-title');

  const heading = element('div', 'afterworld-heading');
  const title = appendText(heading, 'h2', 'afterworld-title', 'What must become possible?');
  title.id = 'inquiry-title';
  appendText(
    heading,
    'p',
    'afterworld-intro',
    'An idea, an existing product, a focused intervention, or a dedicated team. Give us the real situation—not a polished specification.',
  );

  const form = element('form', 'inquiry-form');
  form.noValidate = true;

  const progress = element('div', 'inquiry-progress');
  progress.setAttribute('aria-label', 'Inquiry progress');
  progress.setAttribute('aria-live', 'polite');
  const progressText = appendText(progress, 'span', '', '01 / 05');
  const progressTrack = element('i');
  const progressFill = element('b');
  progressTrack.append(progressFill);
  progress.append(progressTrack);

  const panels: HTMLElement[] = [];

  const start = inquiryPanel(0, 'Where are we beginning?');
  start.append(
    choiceFieldset('', 'starting-point', [
      ['An idea', 'An idea'],
      ['An existing product', 'An existing product'],
      ['One hard problem', 'A focused intervention'],
      ['A dedicated team', 'A dedicated team'],
      ['An agency partnership', 'An agency partnership'],
    ]),
  );
  panels.push(start);

  const situation = inquiryPanel(1, 'What exists now?');
  situation.append(
    textareaField(
      'Describe the situation, problem, or opportunity',
      'situation',
      'Give us the unpolished version. Context, contradictions, and uncertainty are useful.',
    ),
    selectField('Present stage', 'stage', 'Choose the closest description', [
      'Idea only',
      'Research or validation',
      'Design or prototype',
      'Working product',
      'Live and scaling',
      'Existing system needs intervention',
    ]),
  );
  panels.push(situation);

  const outcome = inquiryPanel(2, 'What must become possible?');
  outcome.append(
    textareaField(
      'Describe the outcome—not the feature list',
      'outcome',
      'What changes for the company, users, or system if this succeeds?',
    ),
    textareaField(
      'Known constraints or evidence',
      'constraints',
      'Deadlines, regulation, existing technology, research, dependencies, or risks. Optional.',
      false,
    ),
    selectField('Time horizon', 'timeframe', 'Choose the current expectation', [
      'Urgent intervention',
      '1–3 months',
      '3–6 months',
      '6–12 months',
      'Long-term product relationship',
      'Not defined yet',
    ]),
  );
  panels.push(outcome);

  const engagement = inquiryPanel(3, 'How should the work take shape?');
  engagement.append(
    choiceFieldset('Preferred collaboration', 'collaboration', [
      ['Derive it together', 'Derive the engagement together'],
      ['Complete product team', 'Complete product team'],
      ['Dedicated embedded team', 'Dedicated embedded team'],
      ['Focused intervention', 'Focused intervention'],
      ['Agency partnership', 'Agency partnership'],
    ]),
    choiceFieldset('Budget readiness', 'budget-readiness', [
      ['Allocated', 'Budget allocated'],
      ['Range defined', 'Budget range defined'],
      ['Need a rational estimate', 'Need a rational estimate'],
      ['Still arranging funding', 'Still arranging funding'],
    ]),
  );
  panels.push(engagement);

  const contact = inquiryPanel(4, 'Who should we continue with?');
  const contactGrid = element('div', 'inquiry-contact');
  contactGrid.append(
    inputField('Project name', 'project-name'),
    inputField('Your name', 'name'),
    inputField('Work email', 'email', 'email'),
    inputField('Company', 'company'),
    inputField('Preferred contact channel (optional)', 'contact-channel', 'text', false),
  );
  contact.append(contactGrid);
  panels.push(contact);

  panels.forEach((panel, index) => {
    panel.hidden = index !== 0;
    form.append(panel);
  });

  const controls = element('div', 'inquiry-form__controls');
  const back = inquiryButton('Back', 'back');
  const next = inquiryButton('Continue', 'next');
  const submit = element('button', 'inquiry-control inquiry-control--submit');
  submit.type = 'submit';
  submit.textContent = 'Send the inquiry';
  submit.hidden = true;
  controls.append(back, next, submit);

  const routingNote = appendText(
    form,
    'p',
    'inquiry-routing-note',
    `Your submission is emailed directly to ${INQUIRY_EMAIL}. The website does not retain a separate copy.`,
  );
  routingNote.id = 'inquiry-routing-note';
  routingNote.tabIndex = -1;

  const completion = element('section', 'inquiry-completion');
  completion.hidden = true;
  completion.tabIndex = -1;
  completion.setAttribute('aria-live', 'polite');
  const completionLabel = appendText(completion, 'p', 'inquiry-completion__label', 'Delivered');
  completionLabel.setAttribute('aria-hidden', 'true');
  appendText(completion, 'h3', 'inquiry-completion__title', 'Inquiry sent.');
  const completionMessage = appendText(completion, 'p', 'inquiry-completion__message', '');
  appendText(
    completion,
    'p',
    'inquiry-completion__detail',
    `The full inquiry is now with ${INQUIRY_EMAIL}. We will continue through the email you provided.`,
  );
  const restart = element('button', 'inquiry-control inquiry-control--back');
  restart.type = 'button';
  restart.textContent = 'Send another inquiry';
  completion.append(restart);

  form.setAttribute('aria-describedby', routingNote.id);
  form.prepend(progress);
  form.append(controls, completion);

  let activeStep = 0;
  let startedAt = Date.now();
  const showStep = (step: number): void => {
    activeStep = Math.max(0, Math.min(step, panels.length - 1));
    panels.forEach((panel, index) => {
      panel.hidden = index !== activeStep;
    });
    progressText.textContent = `${String(activeStep + 1).padStart(2, '0')} / ${String(panels.length).padStart(2, '0')}`;
    progressFill.style.transform = `scaleX(${(activeStep + 1) / panels.length})`;
    back.hidden = activeStep === 0;
    next.hidden = activeStep === panels.length - 1;
    submit.hidden = activeStep !== panels.length - 1;
  };

  const validateActiveStep = (): boolean => {
    const controls = panels[activeStep]?.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >('input, textarea, select');
    if (!controls) return true;
    for (const control of controls) {
      if (!control.checkValidity()) {
        control.reportValidity();
        return false;
      }
    }
    return true;
  };

  const validateAllSteps = (): boolean => {
    for (let index = 0; index < panels.length; index += 1) {
      const controls = panels[index]?.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >('input, textarea, select');
      if (!controls) continue;
      for (const control of controls) {
        if (!control.checkValidity()) {
          showStep(index);
          control.reportValidity();
          control.focus();
          return false;
        }
      }
    }
    return true;
  };

  back.addEventListener('click', () => showStep(activeStep - 1));
  next.addEventListener('click', () => {
    if (!validateActiveStep()) return;
    showStep(activeStep + 1);
    panels[activeStep]?.querySelector<HTMLElement>('input, textarea, select')?.focus();
  });

  showStep(0);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (activeStep < panels.length - 1) {
      if (!validateActiveStep()) return;
      showStep(activeStep + 1);
      panels[activeStep]?.querySelector<HTMLElement>('input, textarea, select')?.focus();
      return;
    }
    if (!validateAllSteps()) return;

    const data = new FormData(form);
    const submission: InquirySubmission = {
      startingPoint: String(data.get('starting-point') ?? ''),
      stage: String(data.get('stage') ?? ''),
      situation: String(data.get('situation') ?? ''),
      outcome: String(data.get('outcome') ?? ''),
      constraints: String(data.get('constraints') ?? ''),
      timeframe: String(data.get('timeframe') ?? ''),
      collaboration: String(data.get('collaboration') ?? ''),
      budgetReadiness: String(data.get('budget-readiness') ?? ''),
      projectName: String(data.get('project-name') ?? ''),
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      company: String(data.get('company') ?? ''),
      contactChannel: String(data.get('contact-channel') ?? ''),
    };
    submit.disabled = true;
    submit.textContent = 'Sending…';
    routingNote.classList.remove('is-error');
    routingNote.textContent = `Sending securely to ${INQUIRY_EMAIL}…`;

    const abortController = new AbortController();
    const timeout = window.setTimeout(() => abortController.abort(), 20000);

    try {
      await sendInquiry(submission, startedAt, fetch, abortController.signal);
      window.clearTimeout(timeout);
      panels.forEach((panel) => {
        panel.hidden = true;
      });
      progress.hidden = true;
      controls.hidden = true;
      routingNote.hidden = true;
      completionMessage.textContent = `${submission.projectName} has entered our inquiry queue.`;
      completion.hidden = false;
      completion.focus();
    } catch (error) {
      window.clearTimeout(timeout);
      routingNote.classList.add('is-error');
      routingNote.textContent =
        error instanceof DOMException && error.name === 'AbortError'
          ? `Delivery took too long to confirm. Please try again or email ${INQUIRY_EMAIL} directly.`
          : error instanceof Error
            ? error.message
            : `The inquiry could not be sent. Please email ${INQUIRY_EMAIL} directly.`;
      submit.disabled = false;
      submit.textContent = 'Try again';
      routingNote.focus();
    }
  });

  restart.addEventListener('click', () => {
    form.reset();
    startedAt = Date.now();
    completion.hidden = true;
    progress.hidden = false;
    controls.hidden = false;
    routingNote.hidden = false;
    routingNote.classList.remove('is-error', 'is-success');
    routingNote.textContent = `Your submission is emailed directly to ${INQUIRY_EMAIL}. The website does not retain a separate copy.`;
    submit.disabled = false;
    submit.textContent = 'Send the inquiry';
    showStep(0);
    panels[0]?.querySelector<HTMLElement>('input, textarea, select')?.focus();
  });

  section.append(heading, form);
  return section;
}

export function mountAfterworld(container: HTMLElement): void {
  const afterworld = element('div', 'afterworld');
  afterworld.append(buildPortfolio(), buildTeamStatement(), buildEngagement(), buildInquiry());

  const footer = element('footer', 'site-footer');
  const identity = element('div', 'site-footer__identity');
  const brand = element('a', 'site-footer__brand');
  brand.href = '#top';
  brand.setAttribute('aria-label', 'HackStacks, back to the beginning');
  const mark = element('img');
  mark.src = '/assets/brand/hackstacks-monogram.svg';
  mark.alt = '';
  mark.width = 512;
  mark.height = 512;
  appendText(brand, 'span', '', 'HackStacks');
  brand.prepend(mark);
  appendText(identity, 'p', 'site-footer__statement', 'Precise digital systems.');
  identity.prepend(brand);

  const navigation = element('nav', 'site-footer__navigation');
  navigation.setAttribute('aria-label', 'Footer navigation');
  for (const [label, href] of [
    ['Journey', '#top'],
    ['Selected systems', '#portfolio'],
    ['Ways to work', '#engagement'],
    ['Begin an inquiry', '#inquiry'],
  ] as const) {
    const link = element('a');
    link.href = href;
    link.textContent = label;
    navigation.append(link);
  }

  const contact = element('div', 'site-footer__contact');
  appendText(contact, 'span', 'site-footer__label', 'Direct contact');
  const email = element('a');
  email.href = `mailto:${INQUIRY_EMAIL}`;
  email.textContent = INQUIRY_EMAIL;
  contact.append(email);
  appendText(contact, 'p', '', 'Global · English');

  const baseline = element('div', 'site-footer__baseline');
  appendText(baseline, 'span', '', 'hackstacks.tech');
  appendText(
    baseline,
    'span',
    '',
    'Inquiry submissions are emailed directly. No separate website copy is retained.',
  );

  footer.append(identity, navigation, contact, baseline);
  afterworld.append(footer);
  container.append(afterworld);
  mountPortfolioAnimations(afterworld);
  mountTeamStatement(afterworld);
  mountEngagementAnimation(afterworld);
}
